import type { ReactNode } from 'react'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'
import { AircraftStatusBadge } from './AircraftStatusBadge'

// Minimal structural shape of an aircraft returned by `useAircraftGetAll`,
// kept local so this presentational component doesn't depend on server types.
export type AircraftScheduleCardAircraft = {
  id: string
  registration: string
  type?: string | null
  aircraftStatus: string
  activeMaintenance?: { endDate: string | Date } | null
}

interface AircraftScheduleCardProps {
  // When null/undefined, renders the "All Aircraft" state.
  aircraft?: AircraftScheduleCardAircraft | null
  // Used for the "All Aircraft" subtitle ("{n} total").
  totalCount?: number
  // Trailing slot — a ChevronDown for the trigger, a Check for a selected row.
  trailing?: ReactNode
  showStatusBadge?: boolean
  className?: string
}

function getSubtitle(aircraft: AircraftScheduleCardAircraft): string {
  if (aircraft.aircraftStatus === 'Maintenance' && aircraft.activeMaintenance) {
    return `Until ${dayjs(aircraft.activeMaintenance.endDate).format('MMMM D YYYY')}`
  }
  return aircraft.type ?? ''
}

/**
 * Shared "plane icon + title + subtitle (+ status pill)" block from the
 * Figma Card/Aircraft Schedule design. Reused by the roster trigger card and
 * the rows inside AircraftSelectionDrawer.
 */
export function AircraftScheduleCard({
  aircraft,
  totalCount,
  trailing,
  showStatusBadge = true,
  className,
}: AircraftScheduleCardProps) {
  const isAll = !aircraft

  return (
    <div className={cn('flex items-center gap-2 w-full', className)}>
      {/* Title + subtitle */}
      <div className="flex flex-col min-w-0 flex-1 text-left">
        <p className="text-sm font-semibold text-content-primary-bold truncate">
          {isAll ? 'All Aircraft' : aircraft.registration}
        </p>
        <p className="text-xs text-content-primary-subtle truncate">
          {isAll ? `${totalCount ?? 0} total` : getSubtitle(aircraft)}
        </p>
      </div>

      {/* Status pill */}
      {!isAll && showStatusBadge && (
        <AircraftStatusBadge status={aircraft.aircraftStatus} />
      )}

      {/* Trailing slot (chevron / check) */}
      {trailing && <span className="shrink-0 flex items-center">{trailing}</span>}
    </div>
  )
}
